import { useState } from 'react';
import {
  FINALIDADES_DA_ADEGA,
  FINALIDADE_DESCRICOES,
  FINALIDADE_LABELS,
  type FinalidadeDaAdega,
  type PreferenciasDTO,
} from '@adega/shared';
import { usePreferencias, useRetrato, useSalvarPreferencias } from '../api/queries.js';
import { ErrorBox } from './Feedback.js';

/**
 * Como o dono usa a adega.
 *
 * São só as perguntas cujas respostas o banco não tem como saber. Uva favorita
 * NÃO está entre elas: 26% das garrafas dele são Tannat, e perguntar o que o
 * próprio acervo já responde é o jeito mais rápido de fazer um formulário que
 * ninguém preenche. A tela mostra o que leu e pede confirmação.
 *
 * Tudo opcional. A análise da adega funciona sem nenhuma resposta — só fica
 * mais vaga, e a tela diz isso em vez de bloquear.
 */
export function Preferencias() {
  const { data: preferencias } = usePreferencias();
  const { data: retrato } = useRetrato();
  const salvar = useSalvarPreferencias();

  const [rascunho, setRascunho] = useState<PreferenciasDTO | null>(null);
  const [salvou, setSalvou] = useState(false);
  const [novoEvitado, setNovoEvitado] = useState('');
  const [novaUva, setNovaUva] = useState('');

  if (!preferencias) return null;

  // Escolher entre o que ele tem: a lista vem do acervo inteiro, e não do top 5
  // do gráfico — cortar em cinco deixava de fora uva que ele guarda e gosta.
  const uvasDoAcervo = retrato?.uvasDoAcervo ?? [];
  const jaEscolheu = preferencias.uvasFavoritas.length > 0;

  /**
   * Quem nunca respondeu começa com as três mais guardadas já marcadas — é
   * sugestão, não resposta dele. A sugestão entra na BASE, e não na hora de
   * desenhar: senão desmarcar a última faria as três reaparecerem, e não
   * haveria como dizer "não tenho predileta".
   */
  const base: PreferenciasDTO = {
    ...preferencias,
    uvasFavoritas: jaEscolheu ? preferencias.uvasFavoritas : uvasDoAcervo.slice(0, 3),
  };
  const atual = rascunho ?? base;
  const marcadas = atual.uvasFavoritas;
  const opcoesDeUva = [...new Set([...uvasDoAcervo, ...marcadas])];

  const mudar = (mudanca: Partial<PreferenciasDTO>) => {
    setRascunho({ ...atual, ...mudanca });
    setSalvou(false);
  };

  const alternarUva = (uva: string) =>
    mudar({
      uvasFavoritas: marcadas.includes(uva)
        ? marcadas.filter((outra) => outra !== uva)
        : [...marcadas, uva],
    });

  return (
    <section className="card space-y-4">
      <div>
        <h2 className="font-semibold">Como você usa a adega</h2>
        <p className="text-sm text-adega-muted">
          Três respostas que mudam a análise. Nenhuma é obrigatória.
        </p>
      </div>

      <div>
        <label className="label" htmlFor="garrafas-por-mes">
          Quantas garrafas você bebe por mês?
        </label>
        <input
          id="garrafas-por-mes"
          type="number"
          min={1}
          max={200}
          inputMode="numeric"
          className="field"
          placeholder="Ex.: 2"
          value={atual.garrafasPorMes ?? ''}
          onChange={(e) =>
            mudar({ garrafasPorMes: e.target.value === '' ? null : Number(e.target.value) })
          }
        />
        {/* Esta é a que mais trabalha: sem ela, "52 garrafas fechando em 2030"
            é curiosidade; com ela, vira quantas não dá para alcançar. */}
        <p className="mt-1 text-xs text-adega-muted">
          É o que transforma o acúmulo em conta: sem isso não dá para dizer se alguma garrafa
          vai passar do ponto antes de você chegar nela.
        </p>
      </div>

      <div>
        <span className="label">Para que serve a adega?</span>
        <div role="radiogroup" aria-label="Para que serve a adega" className="space-y-2">
          {FINALIDADES_DA_ADEGA.map((opcao: FinalidadeDaAdega) => {
            const selecionada = atual.finalidade === opcao;
            return (
              <button
                key={opcao}
                type="button"
                role="radio"
                aria-checked={selecionada}
                onClick={() => mudar({ finalidade: opcao })}
                className={`w-full rounded-xl border p-3 text-left transition ${
                  selecionada
                    ? 'border-adega-gold/60 bg-adega-gold/10'
                    : 'border-white/10 bg-adega-surface-2'
                }`}
              >
                <span className={`block text-sm font-medium ${selecionada ? 'text-adega-gold' : ''}`}>
                  {FINALIDADE_LABELS[opcao]}
                </span>
                <span className="mt-0.5 block text-xs leading-relaxed text-adega-muted">
                  {FINALIDADE_DESCRICOES[opcao]}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <span className="label">Alguma uva ou estilo que você evita?</span>
        <p className="mb-2 text-xs text-adega-muted">
          O que você não gosta é invisível para o app — você simplesmente nunca comprou.
        </p>
        <Fichas
          itens={atual.evita}
          aoRemover={(item) => mudar({ evita: atual.evita.filter((x) => x !== item) })}
        />
        <div className="mt-2 flex gap-2">
          <input
            className="field flex-1"
            placeholder="Ex.: Chardonnay com carvalho"
            value={novoEvitado}
            maxLength={60}
            onChange={(e) => setNovoEvitado(e.target.value)}
          />
          <button
            type="button"
            className="btn-secondary"
            disabled={novoEvitado.trim() === '' || atual.evita.length >= 10}
            onClick={() => {
              mudar({ evita: [...atual.evita, novoEvitado.trim()] });
              setNovoEvitado('');
            }}
          >
            Adicionar
          </button>
        </div>
      </div>

      {opcoesDeUva.length > 0 ? (
        <div>
          <span className="label">Suas uvas prediletas</span>
          <p className="mb-2 text-xs text-adega-muted">
            {jaEscolheu
              ? 'Toque para marcar ou desmarcar.'
              : 'Marquei as que você mais guarda. Ajuste como quiser.'}
          </p>
          <ul className="flex flex-wrap gap-2">
            {opcoesDeUva.map((uva) => {
              const marcada = marcadas.includes(uva);
              return (
                <li key={uva}>
                  <button
                    type="button"
                    aria-pressed={marcada}
                    onClick={() => alternarUva(uva)}
                    className={`rounded-full border px-3 py-1 text-sm transition ${
                      marcada
                        ? 'border-adega-gold/60 bg-adega-gold/10 text-adega-gold'
                        : 'border-white/10 bg-adega-surface-2 text-adega-muted'
                    }`}
                  >
                    {marcada ? '✓ ' : ''}
                    {uva}
                  </button>
                </li>
              );
            })}
          </ul>

          {/* Uva que ele gosta e nao guarda nao aparece na lista — e ele
              continua gostando dela. */}
          <div className="mt-2 flex gap-2">
            <input
              className="field flex-1"
              placeholder="Outra uva que você gosta"
              value={novaUva}
              maxLength={60}
              onChange={(e) => setNovaUva(e.target.value)}
            />
            <button
              type="button"
              className="btn-secondary"
              disabled={novaUva.trim() === '' || marcadas.length >= 10}
              onClick={() => {
                const uva = novaUva.trim();
                if (!marcadas.includes(uva)) mudar({ uvasFavoritas: [...marcadas, uva] });
                setNovaUva('');
              }}
            >
              Adicionar
            </button>
          </div>
        </div>
      ) : null}

      {salvar.error ? <ErrorBox erro={salvar.error} /> : null}
      {salvou ? (
        <p className="rounded-lg bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200">
          Guardado. A linha do tempo da adega já considera isto.
        </p>
      ) : null}

      <button
        type="button"
        className="btn-primary w-full"
        disabled={rascunho === null || salvar.isPending}
        onClick={async () => {
          await salvar.mutateAsync(atual);
          setRascunho(null);
          setSalvou(true);
        }}
      >
        {salvar.isPending ? 'Guardando…' : 'Guardar respostas'}
      </button>
    </section>
  );
}

function Fichas({ itens, aoRemover }: { itens: string[]; aoRemover: (item: string) => void }) {
  if (itens.length === 0) return <p className="text-sm text-adega-muted">Nada por enquanto.</p>;

  return (
    <ul className="flex flex-wrap gap-2">
      {itens.map((item) => (
        <li
          key={item}
          className="flex items-center gap-1 rounded-full border border-white/10 bg-adega-surface-2 py-1 pl-3 pr-1 text-sm"
        >
          {/* O texto não é botão. A ficha inteira clicável apagava sem aviso
              quem tocasse nela achando que estava escolhendo — foi o que
              sumiu com a Tannat. */}
          <span>{item}</span>
          <button
            type="button"
            onClick={() => aoRemover(item)}
            aria-label={`Remover ${item}`}
            className="grid h-6 w-6 place-items-center rounded-full text-adega-muted transition hover:bg-white/10 hover:text-adega-text"
          >
            ×
          </button>
        </li>
      ))}
    </ul>
  );
}
